import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, CardHeader, ErrorNotice, Field, Panel, SelectInput } from '@bcecloud/portal-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { errorMessage } from '@/lib/api';
import {
  DEFAULT_LEAD_MEETING_OFFER,
  LEAD_MEETING_OFFER_SETTING,
  LEAD_OWNER_FROM_ACCOUNT_SETTING,
  MEETING_DURATIONS,
  saveSystemSettings,
  WEBSITE_LEADS_DIRECT_SETTING,
  type LeadMeetingOffer,
  type LeadSwitchSetting,
  type MeetingDuration,
  type SystemSettings,
} from '@/lib/events';

interface LeadSwitch {
  readonly key: LeadSwitchSetting;
  readonly label: string;
  readonly description: string;
}

const LEAD_SWITCHES: readonly LeadSwitch[] = [
  {
    key: WEBSITE_LEADS_DIRECT_SETTING,
    label: 'Create website leads in Salesforce',
    description:
      'Contact and quote forms become a Salesforce Lead right away, existing clients included, and the prospect gets our thank-you email. Pardot still gets the prospect but sends nothing. Untick to go back to Pardot creating the Lead.',
  },
  {
    key: LEAD_OWNER_FROM_ACCOUNT_SETTING,
    label: 'Give existing clients’ leads to their account owner',
    description:
      'When the person matches an account by email, their new Lead goes to that account’s owner. Unticked, every website Lead goes to Jessica Hendricks.',
  },
];

/** Where the call offer can appear, with the words the card uses for each. */
const OFFER_SURFACES = [
  {
    key: 'acknowledgementEmail',
    label: 'In the thank-you email',
    description: 'A button that books the call, above the link to browse programs.',
  },
  {
    key: 'thankYouPage',
    label: 'On the thank-you page',
    description: 'Shown with the owner’s first name and photo once the lead is processed.',
  },
] as const satisfies readonly {
  readonly key: keyof LeadMeetingOffer['surfaces'];
  readonly label: string;
  readonly description: string;
}[];

/** The stored offer setting, or the default (off) when it has never been saved. */
function storedOffer(settings: SystemSettings): LeadMeetingOffer {
  return settings[LEAD_MEETING_OFFER_SETTING] ?? DEFAULT_LEAD_MEETING_OFFER;
}

/** The select's value as a call length; anything unexpected keeps the current one. */
function durationFrom(value: string, current: MeetingDuration): MeetingDuration {
  return MEETING_DURATIONS.find((minutes) => String(minutes) === value) ?? current;
}

function sameOffer(a: LeadMeetingOffer, b: LeadMeetingOffer): boolean {
  return (
    a.enabled === b.enabled &&
    a.durationMinutes === b.durationMinutes &&
    a.surfaces.acknowledgementEmail === b.surfaces.acknowledgementEmail &&
    a.surfaces.thankYouPage === b.surfaces.thankYouPage
  );
}

/**
 * The two website-lead switches and the Lead owner's call offer, all off
 * until an admin turns them on. They take effect on the next submission;
 * nothing already in Salesforce changes.
 */
export function WebsiteLeadsCard({ settings }: { readonly settings: SystemSettings }): React.ReactElement {
  const queryClient = useQueryClient();
  const stored = (key: LeadSwitchSetting): boolean => settings[key] === true;
  const [draft, setDraft] = useState<Partial<Record<LeadSwitchSetting, boolean>>>({});
  const [offerDraft, setOfferDraft] = useState<LeadMeetingOffer | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  const offer = offerDraft ?? storedOffer(settings);
  const save = useMutation({
    mutationFn: () =>
      saveSystemSettings([
        ...LEAD_SWITCHES.map((item) => ({ name: item.key, value: isOn(item.key) })),
        { name: LEAD_MEETING_OFFER_SETTING, value: offer },
      ]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      setDraft({});
      setOfferDraft(undefined);
      setSaved(true);
    },
  });

  function isOn(key: LeadSwitchSetting): boolean {
    return draft[key] ?? stored(key);
  }
  function editOffer(next: LeadMeetingOffer): void {
    setSaved(false);
    setOfferDraft(next);
  }
  const dirty =
    LEAD_SWITCHES.some((item) => isOn(item.key) !== stored(item.key)) || !sameOffer(offer, storedOffer(settings));
  const ownerFromAccount = isOn(LEAD_OWNER_FROM_ACCOUNT_SETTING);

  return (
    <Panel className="overflow-hidden xl:col-span-2">
      <div className="p-5 pb-4">
        <CardHeader
          className="mb-0"
          title="Website leads"
          description="How contact and quote form submissions reach Salesforce, and who the prospect can book a call with."
        />
      </div>
      <ul className="divide-y divide-portal-line border-t border-portal-line">
        {LEAD_SWITCHES.map((item) => {
          const id = `lead-switch-${item.key}`;
          return (
            <li key={item.key} className="flex items-start gap-3 px-5 py-3">
              <Checkbox
                id={id}
                checked={isOn(item.key)}
                disabled={save.isPending}
                onCheckedChange={(state) => {
                  setSaved(false);
                  setDraft((prev) => ({ ...prev, [item.key]: state === true }));
                }}
                className="mt-0.5"
              />
              <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                <span className="block text-portal-small font-bold">{item.label}</span>
                <span className="block text-portal-micro text-portal-muted">{item.description}</span>
              </label>
            </li>
          );
        })}
        <li className="px-5 py-3">
          <div className="flex items-start gap-3">
            <Checkbox
              id="lead-meeting-offer-enabled"
              checked={offer.enabled}
              disabled={save.isPending}
              onCheckedChange={(state) => editOffer({ ...offer, enabled: state === true })}
              className="mt-0.5"
            />
            <label htmlFor="lead-meeting-offer-enabled" className="min-w-0 flex-1 cursor-pointer">
              <span className="block text-portal-small font-bold">Offer a call with the Lead owner</span>
              <span className="block text-portal-micro text-portal-muted">
                The prospect can book a short call on their Lead owner’s own Calendly link, or on Jessica
                Hendricks’ link when the owner has none. Only applies while “Give existing clients’ leads to
                their account owner” is on.
              </span>
            </label>
          </div>
          {offer.enabled && !ownerFromAccount ? (
            <p className="mt-2 ml-7 text-portal-micro font-semibold text-portal-muted">
              “Give existing clients’ leads to their account owner” is off, so no call is offered yet.
            </p>
          ) : null}
          {offer.enabled ? (
            <div className="mt-3 ml-7 grid gap-3 sm:grid-cols-2">
              <Field
                label="Call length"
                htmlFor="lead-meeting-offer-duration"
                required
                hint="Owners without a link for this length fall back to the default owner’s link."
              >
                <SelectInput
                  id="lead-meeting-offer-duration"
                  value={offer.durationMinutes}
                  disabled={save.isPending}
                  onChange={(event) =>
                    editOffer({ ...offer, durationMinutes: durationFrom(event.target.value, offer.durationMinutes) })
                  }
                >
                  {MEETING_DURATIONS.map((minutes) => (
                    <option key={minutes} value={minutes}>
                      {minutes} minutes
                    </option>
                  ))}
                </SelectInput>
              </Field>
              <fieldset className="min-w-0">
                <legend className="mb-1.5 text-portal-tiny font-bold">Where to offer it</legend>
                <div className="space-y-2">
                  {OFFER_SURFACES.map((surface) => {
                    const id = `lead-meeting-offer-${surface.key}`;
                    return (
                      <div key={surface.key} className="flex items-start gap-3">
                        <Checkbox
                          id={id}
                          checked={offer.surfaces[surface.key]}
                          disabled={save.isPending}
                          onCheckedChange={(state) =>
                            editOffer({ ...offer, surfaces: { ...offer.surfaces, [surface.key]: state === true } })
                          }
                          className="mt-0.5"
                        />
                        <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                          <span className="block text-portal-small">{surface.label}</span>
                          <span className="block text-portal-micro text-portal-muted">{surface.description}</span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>
            </div>
          ) : null}
        </li>
      </ul>
      <div className="flex items-center gap-3 border-t border-portal-line px-5 py-3">
        <Button disabled={save.isPending || !dirty} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {saved ? <span className="text-portal-micro text-portal-muted">Saved.</span> : null}
      </div>
      {save.isError ? (
        <div className="px-5 pb-4">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}
    </Panel>
  );
}
